/**
 * notification-bell.tsx
 *
 * Bell icon in the sidebar header that shows an unread badge and
 * opens a slide-out panel listing all notifications for the current user.
 *
 * - Polls every 30 s in the background
 * - Click a notification → navigate to linkTo (if set), mark as read
 * - "Mark all read" button in header
 * - Types: job_assigned (briefcase icon), mention (at-sign icon)
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Bell, BriefcaseBusiness, AtSign, CheckCheck, ExternalLink } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import type { Notification } from "@shared/schema";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatAge(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function NotifIcon({ type }: { type: string }) {
  if (type === "job_assigned") {
    return (
      <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
        <BriefcaseBusiness className="w-4 h-4 text-amber-600 dark:text-amber-400" />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
      <AtSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const qc = useQueryClient();

  const { data: notifs = [], refetch } = useQuery<Notification[]>({
    queryKey: ["/api/notifications"],
    queryFn: () => apiRequest("GET", "/api/notifications").then(r => r.json()),
    refetchInterval: 15000,  // background badge stays fresh every 15s
  });

  // Open panel + optimistically mark all as read in the cache so the badge
  // clears immediately, then fire a real refetch to confirm server state.
  function handleOpen() {
    setOpen(true);
    // Optimistic update — stamp all notifications as read in cache
    qc.setQueryData<Notification[]>(["/api/notifications"], old =>
      old?.map(n => n.readAt ? n : { ...n, readAt: new Date().toISOString() }) ?? []
    );
    // Then sync with server
    markAllMutation.mutate();
    refetch();
  }

  const unread = notifs.filter(n => !n.readAt).length;

  const markOneMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("POST", `/api/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/notifications"] }),
  });

  const markAllMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/notifications"] }),
  });

  function handleClick(notif: Notification) {
    if (!notif.readAt) markOneMutation.mutate(notif.id);
    if (notif.linkTo) {
      setOpen(false);
      // linkTo is a full hash route like /#/jobs/12 — strip the leading /# for wouter
      const route = notif.linkTo.replace(/^\/#/, "") || "/";
      navigate(route);
    }
  }

  return (
    <>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        className="relative flex items-center justify-center w-7 h-7 rounded-md hover:bg-sidebar-accent transition-colors"
        title="Notifications"
        data-testid="button-notifications"
        aria-label={`Notifications${unread > 0 ? ` — ${unread} unread` : ""}`}
      >
        <Bell className="w-4 h-4 text-sidebar-foreground/70" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5"
            data-testid="badge-notifications-unread"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Slide-out panel */}
      <Sheet open={open} onOpenChange={v => { if (!v) setOpen(false); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-sm flex flex-col p-0"
          data-testid="panel-notifications"
        >
          <SheetHeader className="px-4 pt-5 pb-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                <SheetTitle className="text-sm">Notifications</SheetTitle>
                {unread > 0 && (
                  <span className="text-[10px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full px-1.5 py-0.5">
                    {unread} new
                  </span>
                )}
              </div>
              {unread > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7 gap-1 text-muted-foreground"
                  onClick={() => markAllMutation.mutate()}
                  disabled={markAllMutation.isPending}
                  data-testid="button-mark-all-read"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all read
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center text-muted-foreground">
                <Bell className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">You'll see job assignments and @mentions here</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {notifs.map(notif => (
                  <li key={notif.id}>
                    <button
                      onClick={() => handleClick(notif)}
                      className={`w-full text-left flex gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors
                        ${!notif.readAt ? "bg-primary/5 dark:bg-primary/10" : ""}`}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <NotifIcon type={notif.type} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-xs font-semibold truncate ${!notif.readAt ? "text-foreground" : "text-muted-foreground"}`}>
                            {notif.title}
                          </p>
                          {notif.linkTo && (
                            <ExternalLink className="w-3 h-3 text-muted-foreground/50 shrink-0 mt-0.5" />
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 line-clamp-2 ${!notif.readAt ? "text-foreground/80" : "text-muted-foreground"}`}>
                          {notif.body}
                        </p>
                        <p className="text-[10px] text-muted-foreground/60 mt-1">
                          {formatAge(notif.createdAt)}
                        </p>
                      </div>
                      {!notif.readAt && (
                        <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
